import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  Work as WorkIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddPositionModal from './AddPositionModal';
import OfficerGroups from '../OfficerGroups';
import AddOfficerGroupModal from '../OfficerGroups/AddOfficerGroupModal';
import { hasPermission } from '../../../utils/permissions';
import {
  bulkDeleteOfficerGroups,
  addOfficerGroup,
} from '../OfficerGroups/slice';
import {
  fetchInit,
  fetchPositions,
  fetchPositionById,
  addPosition,
  updatePosition,
  selectPositions,
  selectGroups,
  selectPositionsLoading,
  selectPositionsError,
  selectPositionsSuccessMessage,
  selectPositionsTotal,
  selectPositionsPage,
  selectPositionsPageSize,
  selectPositionsSortColumn,
  selectPositionsSortDirection,
  selectPositionsFilterName,
  selectSelectedPosition,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  deletePosition,
  bulkDeletePositions,
  clearError,
  clearSuccessMessage,
} from './slice';

// TabPanel component - defined outside to prevent re-creation on each render
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`positions-tabpanel-${index}`}
      aria-labelledby={`positions-tab-${index}`}
      style={{ flex: 1, display: value === index ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}
      {...other}
    >
      <Box sx={{ pt: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{children}</Box>
    </div>
  );
}

function Positions() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const positions = useSelector(selectPositions);
  const groups = useSelector(selectGroups);
  const loading = useSelector(selectPositionsLoading);
  const error = useSelector(selectPositionsError);
  const successMessage = useSelector(selectPositionsSuccessMessage);
  const total = useSelector(selectPositionsTotal);
  const page = useSelector(selectPositionsPage);
  const pageSize = useSelector(selectPositionsPageSize);
  const sortColumn = useSelector(selectPositionsSortColumn);
  const sortDirection = useSelector(selectPositionsSortDirection);
  const filterName = useSelector(selectPositionsFilterName);
  const selectedPosition = useSelector(selectSelectedPosition);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Check permissions
  const canAddPosition = hasPermission('position', 'addPosition');
  const canEditPosition = hasPermission('position', 'editPosition');
  const canDeletePosition = hasPermission('position', 'deletePosition');

  // OfficerGroups permissions
  const canAddOfficerGroup = hasPermission('officergroup', 'addOfficerGroup');
  const canDeleteOfficerGroup = hasPermission('officergroup', 'deleteOfficerGroup');

  // State for OfficerGroups buttons
  const [officerGroupsSelectedIds, setOfficerGroupsSelectedIds] = useState([]);
  const [showOfficerGroupAddDialog, setShowOfficerGroupAddDialog] = useState(false);
  const [showOfficerGroupBulkDeleteDialog, setShowOfficerGroupBulkDeleteDialog] = useState(false);

  // Handlers for OfficerGroups - use useCallback to prevent re-creation
  const handleAddOfficerGroup = useCallback(() => {
    setShowOfficerGroupAddDialog(true);
  }, []);

  const handleCloseAddOfficerGroup = useCallback(() => {
    setShowOfficerGroupAddDialog(false);
  }, []);

  const handleConfirmAddOfficerGroup = useCallback((officerGroupData) => {
    dispatch(addOfficerGroup(officerGroupData));
    setShowOfficerGroupAddDialog(false);
  }, [dispatch]);

  const handleBulkDeleteOfficerGroups = useCallback(() => {
    setShowOfficerGroupBulkDeleteDialog(true);
  }, []);

  // Memoize the setter to prevent re-creation
  const handleOfficerGroupsSelectedIdsChange = useCallback((ids) => {
    setOfficerGroupsSelectedIds(ids);
  }, []);

  // Fetch init data (groups) on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Fetch positions whenever tab changes or filters change
  useEffect(() => {
    dispatch(fetchPositions({ page, pageSize, sortColumn, sortDirection, name: filterName }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, activeTab]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      // Refetch positions to update the list
      dispatch(fetchPositions({ page, pageSize, sortColumn, sortDirection, name: filterName }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

  // Define table columns
  const columns = [
    {
      key: 'code',
      label: t('positions.table.code'),
    },
    {
      key: 'name',
      label: t('positions.table.name'),
    },
    {
      key: 'description',
      label: t('positions.table.description'),
    },
    {
      key: 'groupId',
      label: t('positions.table.group'),
      render: (value, row) => {
        if (!value) return '-';
        const group = groups.find(g => g.id === value);
        return group ? group.name : '-';
      },
    },
    {
      key: 'updatedOn',
      label: t('positions.table.updatedOn'),
      render: (value) => (value ? new Date(value).toLocaleDateString('vi-VN') : '-'),
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    // Fetch position details by ID
    dispatch(fetchPositionById(row.id));
    setShowEditDialog(true);
  };

  // Handle delete action - show confirmation dialog
  const handleDelete = (row) => {
    setPositionToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (positionToDelete?.id) {
      dispatch(deletePosition(positionToDelete.id));
    }
    setShowDeleteDialog(false);
    setPositionToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setPositionToDelete(null);
  };

  // Handle bulk delete - show confirmation dialog
  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeletePositions(selectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset selectedIds when switching tabs
    setSelectedIds([]);
    setOfficerGroupsSelectedIds([]);
    // Refetch init data to update groups for select in modal
    dispatch(fetchInit());
  };

  // Handle add position - show dialog
  const handleAddPosition = () => {
    setShowAddDialog(true);
  };

  // Handle close add position dialog
  const handleCloseAddPosition = () => {
    setShowAddDialog(false);
  };

  // Confirm add position
  const handleConfirmAddPosition = (positionData) => {
    dispatch(addPosition(positionData));
    setShowAddDialog(false);
  };

  // Handle close edit position dialog
  const handleCloseEditPosition = () => {
    setShowEditDialog(false);
  };

  // Confirm edit position
  const handleConfirmEditPosition = (positionData) => {
    dispatch(updatePosition(positionData));
    setShowEditDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BusinessIcon fontSize="small" />
            <Typography variant="body2">{t('positions.breadcrumb.catalog')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('positions.breadcrumb.positions')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('positions.title')}
          </Typography>

          {/* Action Buttons - Show based on active tab */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeTab === 0 && (
              <>
                {canAddPosition && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddPosition}
                    id="addPosition"
                    name="addPosition"
                    data-testid="positions-button-addPosition"
                  >
                    {t('positions.addPosition')}
                  </Button>
                )}
                {canDeletePosition && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDelete}
                    disabled={selectedIds.length === 0}
                    id="deleteSelected"
                    name="deleteSelected"
                    data-testid="positions-button-deleteSelected"
                  >
                    {t('positions.deleteSelected')}
                  </Button>
                )}
              </>
            )}
            {activeTab === 1 && (
              <>
                {canAddOfficerGroup && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddOfficerGroup}
                    id="addOfficerGroup"
                    name="addOfficerGroup"
                    data-testid="positions-button-addOfficerGroup"
                  >
                    {t('officerGroups.addOfficerGroup')}
                  </Button>
                )}
                {canDeleteOfficerGroup && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDeleteOfficerGroups}
                    disabled={officerGroupsSelectedIds.length === 0}
                    id="deleteSelectedOfficerGroups"
                    name="deleteSelectedOfficerGroups"
                    data-testid="positions-button-deleteSelectedOfficerGroups"
                  >
                    {t('officerGroups.deleteSelected')}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="positions tabs">
            <Tab
              icon={<WorkIcon />}
              iconPosition="start"
              label={t('positions.tabs.positions')}
              id="positions-tab-0"
              aria-controls="positions-tabpanel-0"
            />
            <Tab
              icon={<GroupIcon />}
              iconPosition="start"
              label={t('positions.tabs.officerGroups')}
              id="positions-tab-1"
              aria-controls="positions-tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* Positions Tab */}
        <TabPanel value={activeTab} index={0}>
          {/* Filter Section */}
          <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Input
                  label={t('positions.filterByName')}
                  value={filterName}
                  onChange={handleFilterChange}
                  placeholder={t('positions.filterByNamePlaceholder')}
                  id="filterName"
                  name="filterName"
                  data-testid="positions-input-filterName"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
            <Table
              id="positions-table"
              columns={columns}
              data={positions}
              selectable={true}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onEdit={canEditPosition ? handleEdit : null}
              onDelete={canDeletePosition ? handleDelete : null}
              loading={loading}
              emptyMessage={t('positions.noData')}
              idField="id"
              pagination={true}
              page={page}
              pageSize={pageSize}
              totalItems={total}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              sortable={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              sortableColumns={['code', 'name', 'description', 'groupId', 'updatedOn']}
            />
          </Box>
        </TabPanel>

        {/* Officer Groups Tab */}
        <TabPanel value={activeTab} index={1}>
          <OfficerGroups
            onSelectedIdsChange={handleOfficerGroupsSelectedIdsChange}
            onAddClick={handleAddOfficerGroup}
            onBulkDeleteClick={handleBulkDeleteOfficerGroups}
            hideButtons={true}
            isActive={activeTab === 1}
          />
        </TabPanel>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('positions.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('positions.deleteDialog.message', {
              name: positionToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="positions-button-cancelDelete"
          >
            {t('positions.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="positions-button-confirmDelete"
          >
            {t('positions.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('positions.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('positions.bulkDeleteDialog.message', {
              count: selectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="positions-button-cancelBulkDelete"
          >
            {t('positions.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="positions-button-confirmBulkDelete"
          >
            {t('positions.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Position Modal */}
      <AddPositionModal
        open={showAddDialog}
        onClose={handleCloseAddPosition}
        onConfirm={handleConfirmAddPosition}
        groups={groups}
      />

      {/* Edit Position Modal */}
      <AddPositionModal
        open={showEditDialog}
        onClose={handleCloseEditPosition}
        onConfirm={handleConfirmEditPosition}
        positionData={selectedPosition}
        groups={groups}
        editMode={true}
      />

      {/* Add Officer Group Modal */}
      <AddOfficerGroupModal
        open={showOfficerGroupAddDialog}
        onClose={handleCloseAddOfficerGroup}
        onConfirm={handleConfirmAddOfficerGroup}
      />

      {/* Confirm Bulk Delete Officer Groups Dialog */}
      <Dialog
        open={showOfficerGroupBulkDeleteDialog}
        onClose={() => setShowOfficerGroupBulkDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('officerGroups.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('officerGroups.bulkDeleteDialog.message', {
              count: officerGroupsSelectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowOfficerGroupBulkDeleteDialog(false)}
            variant="outlined"
            id="cancelBulkDeleteOfficerGroups"
            name="cancelBulkDeleteOfficerGroups"
            data-testid="positions-button-cancelBulkDeleteOfficerGroups"
          >
            {t('officerGroups.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={() => {
              dispatch(bulkDeleteOfficerGroups(officerGroupsSelectedIds));
              setShowOfficerGroupBulkDeleteDialog(false);
              setOfficerGroupsSelectedIds([]);
            }}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDeleteOfficerGroups"
            name="confirmBulkDeleteOfficerGroups"
            data-testid="positions-button-confirmBulkDeleteOfficerGroups"
          >
            {t('officerGroups.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Positions;
